import React, { useState } from "react";
import axios from "axios";
import { ShieldAlert, Loader2, Check, RefreshCw, AlertCircle } from "lucide-react";
import { API } from "../apiBase";
/**
 * Bannière affichée dans l'espace pro tant que l'email n'a pas été vérifié.
 * - Affiche un input pour le code 6 chiffres
 * - Permet de renvoyer le code
 * - Au succès : appelle onVerified() pour rafraîchir le contexte (email_verified=true)
 */
export default function VerifyEmailBanner({ email, onVerified }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${API}/pro/email/verify/confirm`, { code: code.trim() });
      onVerified && onVerified();
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Code invalide.");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setErr("");
    setResending(true);
    setResent(false);
    try {
      await axios.post(`${API}/pro/email/verify/request`, {});
      setResent(true);
      setTimeout(() => setResent(false), 4000);
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Renvoi impossible.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="rounded-xl border border-amber-300/60 bg-amber-50 p-5 flex items-start gap-4 flex-wrap"
         data-testid="verify-email-banner">
      <div className="grid place-items-center h-10 w-10 rounded-full bg-amber-100 text-amber-600 shrink-0">
        <ShieldAlert size={18} />
      </div>
      <div className="flex-1 min-w-[240px]">
        <div className="text-[#0F2B5B] font-semibold text-[14px]">Vérifiez votre adresse email</div>
        <p className="text-[12.5px] text-slate-600 mt-0.5">
          Un code à 6 chiffres a été envoyé à <span className="font-semibold text-[#0F2B5B]">{email}</span>.
          La vérification est requise avant de pouvoir commander une messagerie MSSanté.
        </p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input data-testid="verify-email-code"
                 className="w-[200px] tracking-[0.4em] font-mono text-center rounded-lg px-3 py-2 text-[14px] bg-white border border-slate-300 text-[#0F2B5B] placeholder:text-slate-300 focus:outline-none focus:border-[#0F2B5B]"
                 inputMode="numeric" maxLength={6} placeholder="123456"
                 value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
          <button onClick={submit} disabled={busy || code.length !== 6}
                  data-testid="verify-email-submit"
                  className="px-4 py-2 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 bg-[#0F2B5B] text-white hover:bg-[#16407f] transition-colors">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            Vérifier
          </button>
          <button onClick={resend} disabled={resending}
                  data-testid="verify-email-resend"
                  className="px-4 py-2 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 bg-white text-[#0F2B5B] border border-slate-300 hover:border-[#0F2B5B] transition-colors">
            {resending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Renvoyer
          </button>
          {resent && (
            <span className="text-[11.5px] text-emerald-600 font-medium inline-flex items-center gap-1">
              <Check size={11} /> Code renvoyé.
            </span>
          )}
        </div>
        {err && (
          <p className="mt-2 text-[12px] text-red-600 inline-flex items-center gap-1">
            <AlertCircle size={12} /> {err}
          </p>
        )}
      </div>
    </div>
  );
}
